import { Injectable, inject } from '@angular/core';
import {SsoRequestBody, SsoView} from '../../../model/sso';
import {PresenceResponse} from '../../../model/user-presence';
import {NetworkService} from '../../../../../../projects/common/src/lib/services/network.service';
import {BaseApiService} from '../base-api.service';
import {FirebaseApi} from '../../firebase-api.service';

/** A join request waiting for an answer (GET /enterprise/invites/pending). */
export interface PendingInvite {
  /** The request's own id: what resend and cancel take. */
  id: string;
  fullName: string;
  email: string;
  photoUrl: string;
  /** When it was last sent. */
  sentOn: number;
}


@Injectable({
  providedIn: 'root',
})
export class EnterpriseApiService extends BaseApiService {

  constructor() {

    super();
  }

  createNewUser(fullName: string, email: string, password: string): Promise<any> {
    return this.post('/enterprise/users/new', {fullName, email, password});
  }

  linkExistingUser(email: string): Promise<any> {
    return this.post('/enterprise/users/link', {email});
  }

  /** Creates the seat and emails the invitee a set-your-password link. */
  inviteUser(fullName: string, email: string): Promise<any> {
    return this.post('/enterprise/users/invite', {fullName, email});
  }

  /** Invalidates every session the member currently has. */
  /**
   * Which of this organization's members are around.
   *
   * Takes no uid list on purpose — the server derives the members from the
   * caller, so this cannot be used to ask whether an arbitrary account is
   * online. Returns liveness only.
   */
  getPresence(): Promise<PresenceResponse> {
    return this.get('/enterprise/users/presence');
  }

  revokeSessions(uid: string): Promise<any> {
    return this.post(`/enterprise/users/${uid}/revoke-sessions`, {});
  }

  /** Re-sends the set-password invitation to a member who never signed in. */
  resendInvite(uid: string): Promise<any> {
    return this.post(`/enterprise/users/${uid}/invite/resend`, {});
  }

  /**
   * Suspends or reactivates a member. Suspension disables the account and
   * revokes its tokens, so it takes effect immediately — it is not a display
   * flag the client could work around.
   */
  setSuspended(uid: string, suspended: boolean): Promise<{suspended: boolean}> {
    return this.put(`/enterprise/users/${uid}/suspended`, {suspended});
  }

  /**
   * Updates an enterprise member. Must be used instead of writing user info
   * straight to Firestore: only this path also updates the `enterpriseRole`
   * custom claim, which is what actually grants the permissions.
   */
  updateUser(uid: string, data: any): Promise<any> {
    return this.put(`/enterprise/users/${uid}`, data);
  }

  setUserRole(uid: string, role: string): Promise<any> {
    return this.updateUser(uid, {enterpriseRole: role});
  }

  acceptEnterpriseAccountLink(notificationId: string): Promise<any> {
    return this.post(`/enterprise/users/link/${notificationId}/accept`, {});
  }

  rejectEnterpriseAccountLink(notificationId: string): Promise<any> {
    return this.post(`/enterprise/users/link/${notificationId}/reject`, {});
  }

  unlinkEnterpriseUser(...uids: string[]): Promise<any> {
    return this.delete(`/enterprise/users/link`, {uids});
  }

  changePassword(uid: string, password: string): Promise<any> {
    return this.post(`/enterprise/users/${uid}/password`, {password});
  }

  /**
   * Deletes members. Each one's projects and folders go to the organisation's
   * owner, in a folder with their name that the owner and the Admins share.
   */
  deleteUsers(...uids: string[]): Promise<{deleted: number} | null> {
    return this.delete(`/enterprise/users`, {uids});
  }

  /** Join requests sent to people who already have an account and have not answered. */
  getPendingInvites(): Promise<{pending: PendingInvite[]}> {
    return this.get('/enterprise/invites/pending');
  }

  /** Emails a waiting join request again (at most once every ten minutes). */
  resendPendingInvite(id: string): Promise<{sentOn: number}> {
    return this.post(`/enterprise/invites/pending/${encodeURIComponent(id)}/resend`, {});
  }

  /** Takes back a join request nobody has answered. */
  cancelPendingInvite(id: string): Promise<any> {
    return this.delete(`/enterprise/invites/pending/${encodeURIComponent(id)}`, null);
  }

  /** The organisation's single sign-on: not set up, requested, sent back, or on. */
  getSso() {
    return this.get<SsoView>('/enterprise/sso');
  }

  /**
   * Ask for single sign-on with the identity provider's details, or correct a
   * request still waiting. Support reviews it and switches it on; customers
   * cannot create a provider in our Firebase project themselves.
   */
  requestSso(body: SsoRequestBody) {
    return this.post<SsoView>('/enterprise/sso/request', body);
  }

  withdrawSsoRequest() {
    return this.delete<SsoView>('/enterprise/sso/request', null);
  }
}
